import { createContext, useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import api from "../utils/API";
export const FeatureFlagsContext = createContext(null);

const DEFAULT_FLAGS = { collaborate: true, documentContrat: true };
let cachedFlags = null;
let pendingFlagsRequest = null;

export function FeatureFlagsProvider({ children }) {
    const [flags, setFlags] = useState(cachedFlags);

    useEffect(() => {
        let active = true;
        const request = pendingFlagsRequest || api.get('/flags')
            .then((res) => {
                cachedFlags = res.data;
                return cachedFlags;
            })
            .catch((error) => {
                cachedFlags = DEFAULT_FLAGS;
                return cachedFlags;
            });
        pendingFlagsRequest = request;
        request.then((data) => {
            if (active) setFlags(data);
        }).finally(() => {
            if (pendingFlagsRequest === request) pendingFlagsRequest = null;
        });
        return () => { active = false; };
    }, []);

    if (!flags) return (
        <Box
            sx={{
                width: '100vw',
                height: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column',
                gap: 3,
                background: '#f8fafc',
            }}
        >
            <Box sx={{ position: 'relative', width: 72, height: 72 }}>
                <svg
                    viewBox="0 0 72 72"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    style={{ width: 72, height: 72, position: 'absolute', top: 0, left: 0 }}
                >
                    <defs>
                        <linearGradient id="ffSpinnerGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" stopColor="#03407b" />
                            <stop offset="100%" stopColor="#02357a" />
                        </linearGradient>
                    </defs>
                    <circle cx="36" cy="36" r="30" stroke="#ebf4fb" strokeWidth="5" />
                    <circle
                        cx="36"
                        cy="36"
                        r="30"
                        stroke="url(#ffSpinnerGrad)"
                        strokeWidth="5"
                        strokeLinecap="round"
                        strokeDasharray="100 88"
                        style={{
                            transformOrigin: '36px 36px',
                            animation: 'ffSpin 0.9s linear infinite',
                        }}
                    />
                </svg>
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: 28,
                        height: 28,
                        borderRadius: '8px',
                        background: 'linear-gradient(135deg, #0556a4, #03407b)',
                        boxShadow: '0 4px 14px rgba(99,102,241,0.45)',
                        animation: 'ffPulse 1.8s ease-in-out infinite',
                    }}
                />
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                <Typography
                    sx={{
                        fontSize: '1rem',
                        fontWeight: 600,
                        color: '#02357a',
                        letterSpacing: '0.02em',
                    }}
                >
                    Chargement des fonctionnalités…
                </Typography>
                <Box sx={{ display: 'flex', gap: '5px', alignItems: 'center', mt: 0.5 }}>
                    {[0, 1, 2].map((i) => (
                        <Box
                            key={i}
                            sx={{
                                width: 5,
                                height: 5,
                                borderRadius: '50%',
                                background: 'linear-gradient(135deg, #0556a4, #03407b)',
                                animation: 'ffDot 1.2s ease-in-out infinite',
                                animationDelay: `${i * 0.2}s`,
                            }}
                        />
                    ))}
                </Box>
            </Box>

            <style>{`
                @keyframes ffSpin {
                    from { transform: rotate(0deg); }
                    to   { transform: rotate(360deg); }
                }
                @keyframes ffPulse {
                    0%, 100% { opacity: 1; transform: translate(-50%, -50%) scale(1); }
                    50%       { opacity: 0.7; transform: translate(-50%, -50%) scale(0.88); }
                }
                @keyframes ffDot {
                    0%, 80%, 100% { transform: scale(0.7); opacity: 0.4; }
                    40%           { transform: scale(1.25); opacity: 1; }
                }
            `}</style>
        </Box>
    );

    return (
        <FeatureFlagsContext.Provider value={flags}>
            {children}
        </FeatureFlagsContext.Provider>
    );
}
